import React, { useEffect, useLayoutEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { subscribeDoneMoment } from '@/src/renderer/lib/doneMoment'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useDoneSound } from '@/src/renderer/lib/sounds'
import { DoneMoment } from '../DoneMoment'
import { getRewardById, getRewardPalette } from '@/src/renderer/lib/data/rewardCatalog'
import { buildVeilClip, measureVeilHoles } from './veilClip'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import './DoneMomentHost.scss'

/**
 * **Die Hülle des Geschafft-Moments** — einmal im Hauptfenster gemountet
 * (`MainLayout`), horcht und zeigt.
 *
 * **Der Schleier deckt das ganze Fenster zu** — Inhalt, Tabs samt Bump,
 * Titelleiste. Ein Schleier, der am Inhalt endet, schnitt quer durchs Bild und
 * las sich wie ein Fehler statt wie Ruhe (gesehen am 17.09.2026). Ausgespart
 * bleibt allein die **Fenstersteuerung**: {@link buildVeilClip} stanzt Löcher,
 * durch das Ampel beziehungsweise Schließen zu sehen UND zu treffen sind.
 *
 * **Warum nicht `components/ui/dialog`:** Der ist `modal`, und das setzt
 * außerhalb `pointer-events: none` und `aria-hidden`. Die Ampel ist bei uns
 * **DOM**, keine Ampel des Systems — sie wäre tot. Ein Programm, dessen
 * Schließen-Knopf nicht mehr geht, fühlt sich wie eine Web-Seite an, und genau
 * das soll es nicht (Nutzer-Vorgabe, 2026-09-17).
 *
 * Darum: `modal={false}`, eigener Schleier (`Dialog.Overlay` rendert ohne
 * `modal` nichts) und der Fokus von Hand auf „Weiter", weil ohne Fokus-Falle
 * sonst ↵ ins Leere liefe.
 *
 * Sie hält keine Aufgabe, nur deren Kennung: Was gezeigt wird, liest sie über
 * `useDataSource()` — auch wenn der Haken im anderen Fenster gefallen ist
 * (ADR 0025).
 */
export const DoneMomentHost: React.FC = () => {
  const source = useDataSource()
  const playDone = useDoneSound()
  const [taskId, setTaskId] = useState<string | null>(null)
  const [clip, setClip] = useState<string>()

  useEffect(() => subscribeDoneMoment(setTaskId), [])

  const task = source.useTask(taskId ?? undefined)
  /*
    `undefined` heißt „wird noch geladen", `null` „gibt es nicht". Gewartet wird
    auf beides: Zeigte die Karte schon vorher, stünde einen Wimpernschlag lang
    die Fassung OHNE Belohnung da und tauschte dann das Bild ein.
  */
  const ready = taskId !== null && task !== undefined

  /*
    **Der Auftritt wartet, bis es ruhig ist** (25.09.2026, Konzept
    `docs/concept/fluessige-bewegung-unter-last.md`). Wird im Fokus-Fenster
    abgehakt, ist der Renderer erst mit der Rückkehr des Hauptfensters
    beschäftigt — die Verzögerungen der Animationen liefen dabei ab, und wenn
    das erste Bild kam, fehlte der Anfang. Bis `whenCalm` freigibt, stehen
    Schleier und Karte pausiert auf ihrem ersten, unsichtbaren Bild
    (`done-moment--waiting`). Solange das Fenster versteckt ist, liefert es
    keine Bilder — das Warten beginnt also erst, wenn es zu sehen ist.

    Die Regel „nach einem Klick nicht warten" greift hier nicht: Der Klick fiel
    in einem anderen Fenster.
  */
  const [calm, setCalm] = useState(false)
  useEffect(() => {
    if (!ready) { setCalm(false); return }
    return whenCalm(() => setCalm(true))
  }, [ready])
  const waiting = calm ? '' : ' done-moment--waiting'


  /*
    **Der Ton kommt mit der Karte**, nicht mit der Anforderung (Nutzer-Vorgabe
    23.09.): Die Karte tritt erst auf, wenn der Schleier halb steht — spielte der
    Ton beim Öffnen, wäre er fast verklungen, während sie noch aufsteigt.

    Er hängt am Beginn ihres Auftritts (`done-moment-rise`, siehe unten am
    `Dialog.Content`) und nicht an einem Timer: Kommt die Meldung aus dem
    Fokus-Fenster, ist das Hauptfenster noch versteckt, und dort drosselt der
    Browser Timer. Die Animation dagegen beginnt erst, wenn das Fenster
    wirklich zu sehen ist.

    Ohne Animation (reduzierte Bewegung) gibt es keinen Auftritt, auf den er
    warten könnte — dann sofort.
  */
  useEffect(() => {
    if (ready && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) playDone()
    // `playDone` ist stabil (`useSound` merkt es sich über Datei und Lautstärke),
    // steht hier also nur, damit die Liste vollständig ist.
  }, [ready, playDone])

  /*
    Das Loch für die Fenstersteuerung wird gemessen, nicht geraten: Die Ampel
    sitzt je nach Anzeigemodus und Betriebssystem woanders. Beim Ändern der
    Fenstergröße neu — sonst wanderte sie unter dem Loch weg.
  */
  useEffect(() => {
    if (!ready) { setClip(undefined); return }
    const measure = () => setClip(buildVeilClip(measureVeilHoles(), {
      width: window.innerWidth,
      height: window.innerHeight,
    }))
    measure()
    window.addEventListener('resize', measure)
    // Windows: Durchs Loch sähe man sonst den hellen Schließen-Knopf — er trägt
    // den Schleierton solange selbst (`DoneMomentHost.scss`).
    const root = document.documentElement
    root.classList.add('done-moment-open')
    return () => {
      window.removeEventListener('resize', measure)
      root.classList.remove('done-moment-open')
    }
  }, [ready])

  /*
    **Der Schleier trägt den Grundton der Belohnung** (F-KS6). Er hängt im
    Portal am `body`, die Karte darin setzt ihre Farben selbst — der Schleier
    bekommt seine vom Wurzelelement, solange der Moment steht. Ohne Belohnung
    bleibt es beim Blau aus `globals.scss`.
  */
  const veilBase = ready ? getRewardPalette(getRewardById(task?.rewardId)).base : null
  // Vor dem ersten Bild — sonst blendete der Schleier einen Wimpernschlag in Blau ein.
  useLayoutEffect(() => {
    if (!veilBase) return
    const root = document.documentElement
    root.style.setProperty('--done-moment-deep', veilBase)
    return () => { root.style.removeProperty('--done-moment-deep') }
  }, [veilBase])

  if (!ready) return null

  return (
    <Dialog.Root open onOpenChange={open => { if (!open) setTaskId(null) }} modal={false}>
      <Dialog.Portal>
        <div
          className={`done-moment-veil${waiting}`}
          data-testid="done-moment-veil"
          style={clip ? { clipPath: clip } : undefined}
          onClick={() => setTaskId(null)}
        />
        <Dialog.Content
          className={`done-moment-shell${waiting}`}
          aria-describedby={undefined}
          /*
            **Fokuswechsel schließen den Moment NICHT.** Kommt er aus dem Fokus-Fenster,
            holt der Main-Prozess unmittelbar danach das Hauptfenster zurück —
            und dieses Fokussieren wertete Radix als „Interaktion außerhalb" und
            räumte die Karte sofort wieder weg. Mal blitzte sie kurz auf, mal war
            sie nie zu sehen (gefunden am 17.09.2026 beim Nachstellen für eine
            Aufnahme; der E2E-Lauf hatte es zweimal zufällig überlebt).

            Weggeklickt wird weiterhin: über den Schleier, „Weiter", den
            Belohnungsknopf und Esc.
          */
          onFocusOutside={event => event.preventDefault()}
          onOpenAutoFocus={event => event.preventDefault()}
          // Die Teile in der Karte haben eigene Auftritte; der Ton gehört dem der Karte.
          onAnimationStart={event => { if (event.animationName === 'done-moment-rise') playDone() }}
        >
          <DoneMoment
            rewardId={task?.rewardId}
            onClose={() => setTaskId(null)}
            Heading={Dialog.Title}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
